import { useMemo } from 'react';
import { EventPageFragment } from '@graphql/generated';
import { useLayout } from '@shared/context/LayoutProvider';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGetGuestSitePages } from '@shared/hooks/useGetGuestSitePages';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';

/**
 * Determines whether the ViewDetailsArrow component should be displayed and returns the next page slug.
 *
 * @param pages - Array of event pages to derive the next page slug from
 * @returns An object with `shouldShow` (boolean) and `nextPageSlug` (string | undefined)
 */
export const useShouldShowViewDetailsArrow = (pages?: readonly EventPageFragment[] | null): Readonly<{ shouldShow: boolean; nextPageSlug: string | undefined }> => {
  const { layout } = useLayout();
  const [isMobile] = useResponsive({ values: { xxs: true, xs: true, sm: false } });
  const { eventHandle } = useEventInfo();
  const { pages: guestSitePages } = useGetGuestSitePages({ pages: pages ?? [] });
  const { findWelcomePage } = useWelcomePage();

  const nextPageSlug = useMemo(() => {
    if (!guestSitePages || guestSitePages.length === 0) {
      return undefined;
    }

    const welcomePageIndex = guestSitePages.findIndex(page => findWelcomePage(page.pageSlug, page.type).isWelcomePage);

    if (welcomePageIndex === -1) {
      return undefined;
    }

    const nextPage = welcomePageIndex === guestSitePages.length - 1 ? null : guestSitePages[welcomePageIndex + 1];
    return nextPage?.pageSlug;
  }, [guestSitePages, findWelcomePage]);

  const shouldShow = layout === 'aloha' && !isMobile && !!nextPageSlug && !!eventHandle;

  return { shouldShow, nextPageSlug };
};
